import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
} from "react";

/**
 * Global context to track all active file uploads across the app.
 * Any useFileUpload hook call (single or batch) registers here automatically,
 * allowing the UploadProgressPanel to display a stacked progress UI.
 */
const UploadProgressContext = createContext(null);

export const useUploadProgress = () => useContext(UploadProgressContext);

export const UploadProgressProvider = ({ children }) => {
  const [uploads, setUploads] = useState({});
  const dismissTimers = useRef({});

  /**
   * Register a new upload when it starts.
   * @param {string} id - Unique upload identifier
   * @param {Object} info - { fileName, fileSize }
   */
  const registerUpload = useCallback((id, { fileName, fileSize }) => {
    setUploads((prev) => ({
      ...prev,
      [id]: {
        id,
        fileName,
        fileSize,
        progress: 0,
        status: "uploading", // uploading | success | failed
        uploadSpeed: 0,
        remainingTime: null,
        retryFn: null,
        startedAt: Date.now(),
      },
    }));
  }, []);

  /**
   * Update progress/speed/time for an in-flight upload.
   */
  const updateUpload = useCallback((id, updates) => {
    setUploads((prev) => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], ...updates } };
    });
  }, []);

  /**
   * Mark an upload as successfully completed and auto-dismiss after 3s.
   */
  const completeUpload = useCallback((id) => {
    setUploads((prev) => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: { ...prev[id], progress: 100, status: "success", retryFn: null },
      };
    });

    // Auto-dismiss after 3 seconds
    dismissTimers.current[id] = setTimeout(() => {
      setUploads((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      delete dismissTimers.current[id];
    }, 3000);
  }, []);

  /**
   * Mark an upload as failed and store the retry function.
   * @param {string} id
   * @param {Function} retryFn - Async function to retry the upload
   */
  const failUpload = useCallback((id, retryFn) => {
    setUploads((prev) => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], status: "failed", retryFn } };
    });
  }, []);

  /**
   * Manually dismiss/remove an upload entry from the panel.
   */
  const dismissUpload = useCallback((id) => {
    if (dismissTimers.current[id]) {
      clearTimeout(dismissTimers.current[id]);
      delete dismissTimers.current[id];
    }
    setUploads((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  /**
   * Re-register an upload that is being retried (reset to uploading state).
   */
  const retryUpload = useCallback((id) => {
    if (dismissTimers.current[id]) {
      clearTimeout(dismissTimers.current[id]);
      delete dismissTimers.current[id];
    }
    setUploads((prev) => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: {
          ...prev[id],
          progress: 0,
          status: "uploading",
          uploadSpeed: 0,
          remainingTime: null,
        },
      };
    });
  }, []);

  return (
    <UploadProgressContext.Provider
      value={{
        uploads,
        registerUpload,
        updateUpload,
        completeUpload,
        failUpload,
        dismissUpload,
        retryUpload,
      }}
    >
      {children}
    </UploadProgressContext.Provider>
  );
};
